import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Friend, FriendRequest, PublicProfile, SuggestedUser, CloseFriend } from "@/types";

export function useFriends() {
  return useQuery({
    queryKey: ["friends"],
    queryFn: () => api.get<Friend[]>("/api/friends")
  });
}

export function useTodaysBirthdays() {
  return useQuery({
    queryKey: ["birthdays-today"],
    queryFn: () => api.get<{ id: string; username: string; display_name: string; avatar_url: string | null }[]>("/api/friends/birthdays-today")
  });
}

export function useSetFavoriteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ friendId, favorite }: { friendId: string; favorite: boolean }) =>
      api.post(`/api/friends/${friendId}/favorite`, { favorite }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["friends"] })
  });
}

export function useCloseFriends() {
  return useQuery({
    queryKey: ["close-friends"],
    queryFn: () => api.get<CloseFriend[]>("/api/friends/close-friends")
  });
}

export function useSetCloseFriend() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ friendId, isCloseFriend }: { friendId: string; isCloseFriend: boolean }) =>
      api.post(`/api/friends/${friendId}/close-friend`, { isCloseFriend }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["close-friends"] })
  });
}

export function useIncomingRequests() {
  return useQuery({
    queryKey: ["friend-requests"],
    queryFn: () => api.get<FriendRequest[]>("/api/friends/requests")
  });
}

export function useSendFriendRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (toUserId: string) => api.post("/api/friends/requests", { toUserId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["friends"] });
      queryClient.invalidateQueries({ queryKey: ["outgoing-friend-requests"] });
    }
  });
}

export function useOutgoingRequests() {
  return useQuery({
    queryKey: ["outgoing-friend-requests"],
    queryFn: () => api.get<FriendRequest[]>("/api/friends/requests/outgoing")
  });
}

export function useCancelFriendRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetUserId: string) => api.delete(`/api/friends/requests/outgoing/${targetUserId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["outgoing-friend-requests"] })
  });
}

export function useRespondToRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      api.post(`/api/friends/requests/${id}/${accept ? "accept" : "reject"}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["friend-requests"] });
      queryClient.invalidateQueries({ queryKey: ["friends"] });
    }
  });
}

export function useUnfriend() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete(`/api/friends/${userId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["friends"] })
  });
}

export function useUserSearch(query: string) {
  return useQuery({
    queryKey: ["user-search", query],
    queryFn: () => api.get<PublicProfile[]>(`/api/users/search?q=${encodeURIComponent(query)}`),
    enabled: query.trim().length > 0
  });
}

export function useProfile(userId: string | undefined) {
  return useQuery({
    queryKey: ["profile", userId],
    queryFn: () => api.get<PublicProfile>(`/api/users/${userId}`),
    enabled: !!userId
  });
}

export function useSearchUsers(query: string) {
  return useQuery({
    queryKey: ["users-search", query],
    queryFn: () => api.get<PublicProfile[]>(`/api/users/search?q=${encodeURIComponent(query)}`),
    enabled: query.trim().length > 0
  });
}

export function useMutualFriends(userId: string | undefined) {
  return useQuery({
    queryKey: ["mutual-friends", userId],
    queryFn: () => api.get<Friend[]>(`/api/friends/mutual/${userId}`),
    enabled: !!userId
  });
}

/** "People you may know" - ranked by mutual friends, then by follower count. */
export function useSuggestedUsers() {
  return useQuery({
    queryKey: ["suggested-users"],
    queryFn: () => api.get<SuggestedUser[]>("/api/users/suggestions")
  });
}

export function useSetNickname() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, nickname }: { userId: string; nickname: string }) =>
      api.put(`/api/friends/${userId}/nickname`, { nickname }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["friends"] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["conversation"] });
    }
  });
}

export function useClearNickname() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete(`/api/friends/${userId}/nickname`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["friends"] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["conversation"] });
    }
  });
}
